import React, { useRef, useState } from 'react';

import type { CodeBlockRedlineItem, ProposalSuggestion, UUID } from '@aline/common';
import { Select, sortPlaybookRulePositionsByRank } from '@aline/common';
import { link } from '@aline/routes';
import { IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { CircleOff, Pencil, RefreshCw, Sparkles } from 'lucide-react';
import { AlineTooltip, RouterLink } from 'src/components';
import { VertDotsIcon } from 'src/components/icons/VertDotsIcon';
import { Apollo } from 'src/gql/generated';
import { openLinkFromAnchorClick } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { PLAYBOOK_TONES } from './playbook-colors';
import type {
  PlaybookPositionBlockView,
  PlaybookRunPositionBlockView,
  RuleResolveAction,
  RunPositionPresentation,
} from './playbook-rule-view';
import {
  getHasPlaybookRuleCardBody,
  getParsedProposalsByPositionId,
  getPlaybookRulePositionBlocks,
  getReviewingPositionIdFromProposals,
  getRuleResolveAction,
  getRunPositionPresentation,
} from './playbook-rule-view';
import type { ParsedProposal, SuggestionReviewVerb } from './playbook-run-hooks';
import type { PlaybookRule, PlaybookRuleJob } from './playbook-run-state';
import {
  getIsRuleResolved,
  getIsRuleReviewable,
  getIsRuleRunFailed,
  getRuleJob,
  getRuleStatus,
} from './playbook-run-state';
import { PLAYBOOK_CHIP_LABELS, PLAYBOOK_RULE_STATUS_CHIPS, PlaybookChip } from './PlaybookChip';
import {
  PlaybookPositionBlock,
  ResolveActionButton,
  ResolvedNotice,
} from './PlaybookPositionBlock';
import type { SuggestionReviewProps } from './PlaybookPositionBlock';
import { useIsStuck } from './use-is-stuck';

type ChipDescriptor = {
  label: string;
  tone: React.ComponentProps<typeof PlaybookChip>['tone'];
};

const MENU_ICON_SIZE = 16;

const useStyles = makeStyles()(theme => {
  const hairline = `1px solid ${theme.appColors.rule}`;

  return {
    body: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      padding: theme.spacing(0, 0, 1.5),
    },
    /**
     * No box of its own: a hairline divides one rule from the next, and the
     * indented body reads as belonging to the name above it. The position
     * blocks inside are the only cards in the panel.
     *
     * `scroll-margin-top` is set inline from the category header's live height
     * so Next / Previous parks this rule below that sticky header instead of
     * sliding the number and title underneath it.
     */
    card: {
      borderTop: hairline,
    },
    /**
     * Sticks under the category header while its own body scrolls, so a long
     * rule never leaves you reading positions with no idea which rule they
     * belong to. The card bounds it, so it hands off to the next rule's title.
     * The stacking order has to be positive, not 0: the suggestion rows below
     * are positioned too, and at 0 they would paint over it on tree order.
     */
    header: {
      alignItems: 'center',
      background: theme.palette.background.paper,
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'space-between',
      padding: theme.spacing(1.25, 0),
      position: 'sticky',
      zIndex: 1,
    },
    headerLabel: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      gap: theme.spacing(1),
      minWidth: 0,
    },
    headerRight: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      gap: theme.spacing(0.5),
    },
    /**
     * Bottom hairline only while pinned, so scrolling body doesn't run under
     * the title. Top edge is the category header's stuck shadow.
     */
    headerStuck: {
      boxShadow: `0 1px 0 ${theme.appColors.rule}`,
    },
    menuButton: {
      color: theme.appColors.secondaryType,
      padding: theme.spacing(0.25),
    },
    menuIcon: {
      '&.MuiListItemIcon-root': {
        color: theme.appColors.iconsType,
        minWidth: 22,
      },
    },
    name: {
      fontSize: 14,
      fontWeight: 600,
      minWidth: 0,
    },
    number: {
      alignItems: 'center',
      backgroundColor: theme.appColors.darkBackground,
      border: hairline,
      borderRadius: '50%',
      color: theme.appColors.secondaryType,
      display: 'inline-flex',
      flexShrink: 0,
      fontSize: 12,
      fontWeight: 700,
      height: 20,
      justifyContent: 'center',
      width: 20,
    },
    sentinel: {
      height: 1,
      marginBottom: -1,
      pointerEvents: 'none',
      visibility: 'hidden',
    },
    staleBanner: {
      alignItems: 'center',
      backgroundColor: PLAYBOOK_TONES.review.background,
      border: `1px solid ${PLAYBOOK_TONES.review.border}`,
      borderRadius: 6,
      display: 'flex',
      fontSize: 12,
      gap: theme.spacing(1),
      justifyContent: 'space-between',
      padding: theme.spacing(1, 1.5),
    },
    staleRerun: {
      alignItems: 'center',
      background: 'none',
      border: 'none',
      color: PLAYBOOK_TONES.review.color,
      cursor: 'pointer',
      display: 'flex',
      // The label is the action: it holds one line while the message wraps.
      flexShrink: 0,
      fontSize: 12,
      fontWeight: 600,
      gap: theme.spacing(0.5),
      padding: 0,
      whiteSpace: 'nowrap',
    },
  };
});

export function getPlaybookRuleElementId(ruleId: string): string {
  return `playbook-rule-${ruleId}`;
}

export interface PlaybookRuleActions {
  clearResolution: (jobId: string) => Promise<unknown>;
  /** Drafts an edit request for the rule in the chat, for the user to finish and send. */
  editRuleWithAi: (rule: Pick<PlaybookRule, 'id' | 'title'>) => void;
  rerunRule: (ruleId: string) => Promise<unknown>;
  resolve: (options: {
    jobId: string;
    kind: Apollo.PlaybookRuleResolutionKind;
    positionId?: string;
  }) => Promise<unknown>;
  /** One review decision; accept materializes before recording (ADR 0006). */
  reviewSuggestion: (options: {
    jobId: string;
    suggestion: ProposalSuggestion;
    verb: SuggestionReviewVerb;
  }) => Promise<unknown>;
}

export interface PlaybookRuleCardProps {
  actions: PlaybookRuleActions;
  /** Judged against the live document by the detail panel, per suggestion. */
  getIsStale: (previewCards: CodeBlockRedlineItem[]) => boolean;
  /**
   * Accepts whose mutation round trip is still in flight: already in the
   * document, not yet in the run data. Overlaid on the stored decisions so
   * the position never reads as stale from its own just-landed accept.
   */
  pendingAcceptedSuggestionIds: ReadonlySet<string>;
  playbookId: UUID;
  rule: PlaybookRule;
  ruleNumber: number;
  /** Where the title parks when stuck: the category header's live height. */
  stickyTop: number;
}

/** The rule's outcome chip; a not-run rule has no outcome to report. */
function getRuleChip(rule: PlaybookRule): ChipDescriptor | null {
  const status = getRuleStatus(rule);
  if (status === 'not-run') return null;
  return PLAYBOOK_RULE_STATUS_CHIPS[status];
}

function getSuggestionReview(options: {
  actions: PlaybookRuleActions;
  isProposalStale: boolean;
  job: PlaybookRuleJob;
  parsedProposal: ParsedProposal;
  reviewControlsDisabled: boolean;
  ruleId: string;
}): SuggestionReviewProps {
  const { actions, isProposalStale, job, parsedProposal, reviewControlsDisabled, ruleId } = options;
  return {
    decisions: parsedProposal.decisions,
    disabled: reviewControlsDisabled,
    isStale: isProposalStale,
    onAccept: suggestion =>
      void actions.reviewSuggestion({ jobId: job.id, suggestion, verb: 'accept' }),
    onReject: suggestion =>
      void actions.reviewSuggestion({ jobId: job.id, suggestion, verb: 'reject' }),
    onRerun: () => void actions.rerunRule(ruleId),
    onUnreject: suggestion =>
      void actions.reviewSuggestion({ jobId: job.id, suggestion, verb: 'unreject' }),
  };
}

function getIsRunPositionBlock(
  block: PlaybookPositionBlockView
): block is PlaybookRunPositionBlockView {
  return block.source === 'run';
}

function PlaybookRulePositionList({
  actions,
  blocks,
  getIsStale,
  job,
  parsedProposalByPositionId,
  reviewingPositionId,
  ruleId,
}: {
  actions: PlaybookRuleActions;
  blocks: PlaybookPositionBlockView[];
  getIsStale: (previewCards: CodeBlockRedlineItem[]) => boolean;
  job: PlaybookRuleJob | null;
  parsedProposalByPositionId: Map<string, ParsedProposal>;
  reviewingPositionId: string | null;
  ruleId: string;
}): JSX.Element {
  if (!job)
    return (
      <>
        {blocks.map(block => (
          <PlaybookPositionBlock
            key={block.positionId}
            chip={{ label: PLAYBOOK_CHIP_LABELS.notRun, tone: 'not-run' }}
            label={block.label}
            suggestions={null}
            text={block.text}
          />
        ))}
      </>
    );

  return (
    <>
      {blocks.filter(getIsRunPositionBlock).map(block => (
        <PlaybookRunPositionBlock
          key={block.positionId}
          actions={actions}
          block={block}
          getIsStale={getIsStale}
          job={job}
          parsedProposal={parsedProposalByPositionId.get(block.positionId) ?? null}
          reviewingPositionId={reviewingPositionId}
          ruleId={ruleId}
        />
      ))}
    </>
  );
}

function PlaybookRunPositionBlock({
  actions,
  block,
  getIsStale,
  job,
  parsedProposal,
  reviewingPositionId,
  ruleId,
}: {
  actions: PlaybookRuleActions;
  block: PlaybookRunPositionBlockView;
  getIsStale: (previewCards: CodeBlockRedlineItem[]) => boolean;
  job: PlaybookRuleJob;
  parsedProposal: ParsedProposal | null;
  reviewingPositionId: string | null;
  ruleId: string;
}): JSX.Element {
  const presentation = getRunPositionPresentation({
    getIsStale,
    isFallback: block.isFallback,
    parsedProposal,
    resolution: job.resolution,
    result: block.result,
    reviewingPositionId,
  });

  return (
    <PlaybookPositionBlock
      chip={presentation.chip}
      dimmed={presentation.dimmed}
      explanation={presentation.explanation}
      label={block.label}
      suggestionReview={
        presentation.showSuggestionReview && parsedProposal
          ? getSuggestionReview({
              actions,
              isProposalStale: presentation.isProposalStale,
              job,
              parsedProposal,
              reviewControlsDisabled: presentation.locks.reviewControlsDisabled,
              ruleId,
            })
          : null
      }
      suggestions={parsedProposal?.suggestions ?? null}
      text={block.text}
    >
      {presentation.showFallbackAccept && (
        <FallbackPositionAction
          actions={actions}
          job={job}
          positionId={block.positionId}
          presentation={presentation}
        />
      )}
    </PlaybookPositionBlock>
  );
}

/**
 * Accepting this fallback, or the record of it once accepted. Anyone on the
 * document can be the one who accepted, so the settled state names them.
 */
function FallbackPositionAction({
  actions,
  job,
  positionId,
  presentation,
}: {
  actions: PlaybookRuleActions;
  job: PlaybookRuleJob;
  positionId: string;
  presentation: RunPositionPresentation;
}): JSX.Element {
  if (presentation.ownFallbackActive)
    return (
      <ResolvedNotice
        label={getFallbackAcceptedLabel(job.resolution)}
        undoDisabled={presentation.othersDisabled}
        onUndo={() => void actions.clearResolution(job.id)}
      />
    );

  return (
    <ResolveActionButton
      disabled={presentation.othersDisabled}
      label="Accept Fallback Position"
      onResolve={() =>
        void actions.resolve({
          jobId: job.id,
          kind: Apollo.PlaybookRuleResolutionKind.Fallback,
          positionId,
        })
      }
    />
  );
}

function getFallbackAcceptedLabel(resolution: PlaybookRuleJob['resolution']): string {
  const acceptedBy = resolution?.by.displayName.trim();
  if (!acceptedBy) return 'Fallback accepted';
  return `Fallback accepted by ${acceptedBy}`;
}

/**
 * Resolving the rule as a whole, or the record of it once resolved. It acts
 * on the rule, so it sits below the position blocks rather than inside one.
 * The button never meets a mid-review lock: it shows only when the rule has no
 * edit to decide, and a review is a decision on an edit.
 */
function RuleResolveActionControl({
  action,
  actions,
  job,
}: {
  action: RuleResolveAction;
  actions: PlaybookRuleActions;
  job: PlaybookRuleJob;
}): JSX.Element {
  if (action === 'resolved')
    return (
      <ResolvedNotice
        label={getRuleResolvedLabel(job.resolution)}
        undoDisabled={false}
        onUndo={() => void actions.clearResolution(job.id)}
      />
    );

  return (
    <ResolveActionButton
      disabled={false}
      label="Mark as Resolved"
      onResolve={() =>
        void actions.resolve({ jobId: job.id, kind: Apollo.PlaybookRuleResolutionKind.Manual })
      }
    />
  );
}

function getRuleResolvedLabel(resolution: PlaybookRuleJob['resolution']): string {
  const resolvedBy = resolution?.by.displayName.trim();
  if (!resolvedBy) return 'Resolved';
  return `Resolved by ${resolvedBy}`;
}

export function PlaybookRuleCard({
  actions,
  getIsStale,
  pendingAcceptedSuggestionIds,
  playbookId,
  rule,
  ruleNumber,
  stickyTop,
}: PlaybookRuleCardProps): JSX.Element {
  const { classes, cx } = useStyles();
  const { sentinelRef, stuck } = useIsStuck(stickyTop);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  const job = getRuleJob(rule);
  const sortedPositions = sortPlaybookRulePositionsByRank(rule.positions);
  const blocks = getPlaybookRulePositionBlocks({ job, livePositions: rule.positions });
  const isReviewable = getIsRuleReviewable(job);
  const isResolved = getIsRuleResolved(job);
  const isRunFailed = getIsRuleRunFailed(job);
  const showSkip = isReviewable && !isResolved;
  const ruleName = rule.title.trim() || sortedPositions[0]?.ruleText || 'Untitled rule';
  const parsedProposalByPositionId = getParsedProposalsByPositionId(
    job?.proposals ?? [],
    pendingAcceptedSuggestionIds
  );
  const reviewingPositionId = getReviewingPositionIdFromProposals(
    parsedProposalByPositionId,
    isResolved
  );
  const resolveAction = getRuleResolveAction(job);
  const headerChip = getRuleChip(rule);
  const ruleStatus = getRuleStatus(rule);
  const editHref = link.PlaybooksDetailRule({ playbookId, ruleId: rule.id });
  const showFailedBanner = isRunFailed && Boolean(job?.failedReason);
  const hasBody = getHasPlaybookRuleCardBody({
    failedReason: showFailedBanner ? job?.failedReason : null,
    positionBlockCount: blocks.length,
  });

  return (
    <>
      <div
        className={classes.card}
        data-cy={Select.document.editor.rightDrawer.ai.panel.ruleCard.container}
        id={getPlaybookRuleElementId(rule.id)}
        style={{ scrollMarginTop: stickyTop }}
      >
        <div ref={sentinelRef} className={classes.sentinel} />
        <div
          className={cx(classes.header, stuck && classes.headerStuck)}
          data-playbook-sticky-title=""
          style={{ top: stickyTop }}
        >
          <div className={classes.headerLabel}>
            <span className={classes.number}>{ruleNumber}</span>
            <Typography className={classes.name}>{ruleName}</Typography>
          </div>
          <div className={classes.headerRight}>
            {headerChip && <PlaybookChip label={headerChip.label} tone={headerChip.tone} />}
            <AlineTooltip arrow placement="top" title="Rule actions">
              <IconButton
                ref={menuButtonRef}
                aria-label={`Actions for ${ruleName}`}
                className={classes.menuButton}
                size="small"
                onClick={() => setMenuOpen(true)}
              >
                <VertDotsIcon />
              </IconButton>
            </AlineTooltip>
          </div>
        </div>
        {hasBody && (
          <div className={classes.body}>
            {showFailedBanner && (
              <div className={classes.staleBanner}>
                <span>This rule failed to run: {job?.failedReason}</span>
                <button
                  className={classes.staleRerun}
                  type="button"
                  onClick={() => actions.rerunRule(rule.id)}
                >
                  <RefreshCw size={12} /> Rerun rule
                </button>
              </div>
            )}
            <PlaybookRulePositionList
              actions={actions}
              blocks={blocks}
              getIsStale={getIsStale}
              job={job}
              parsedProposalByPositionId={parsedProposalByPositionId}
              reviewingPositionId={reviewingPositionId}
              ruleId={rule.id}
            />
            {job && resolveAction && (
              <RuleResolveActionControl action={resolveAction} actions={actions} job={job} />
            )}
          </div>
        )}
      </div>
      <Menu
        anchorEl={menuButtonRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setMenuOpen(false)}
      >
        <MenuItem
          dense
          component={RouterLink}
          to={editHref}
          onClick={event => {
            setMenuOpen(false);
            openLinkFromAnchorClick(event, editHref, {
              newTab: true,
              title: ruleName,
            });
          }}
        >
          <ListItemIcon className={classes.menuIcon}>
            <Pencil size={MENU_ICON_SIZE} />
          </ListItemIcon>
          Edit rule
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            setMenuOpen(false);
            actions.editRuleWithAi(rule);
          }}
        >
          <ListItemIcon className={classes.menuIcon}>
            <Sparkles size={MENU_ICON_SIZE} />
          </ListItemIcon>
          Edit rule with AI
        </MenuItem>
        {showSkip && (
          <MenuItem
            dense
            disabled={reviewingPositionId !== null}
            onClick={() => {
              setMenuOpen(false);
              if (!job) return;
              void actions.resolve({
                jobId: job.id,
                kind: Apollo.PlaybookRuleResolutionKind.Manual,
              });
            }}
          >
            <ListItemIcon className={classes.menuIcon}>
              <CircleOff size={MENU_ICON_SIZE} />
            </ListItemIcon>
            Skip rule
          </MenuItem>
        )}
        {ruleStatus !== 'running' && (
          <MenuItem
            dense
            onClick={() => {
              setMenuOpen(false);
              void actions.rerunRule(rule.id);
            }}
          >
            <ListItemIcon className={classes.menuIcon}>
              <RefreshCw size={MENU_ICON_SIZE} />
            </ListItemIcon>
            {ruleStatus === 'not-run' ? 'Run rule' : 'Rerun rule'}
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
